import * as d3 from 'd3';
import _ from 'lodash';

import RenderingState from '../RenderingState';
import VirtualDomIndex from './VirtualDomIndex';

export default class VirtualDomLayer {
  indices: object;
  elements: any[];
  keyFramesOpacity: any[];
  opacityScaling: any;
  name: string;
  _showAtInitialZoom: boolean;

  constructor({
    name,
    elements = [],
    keyFramesOpacity = [
      [0, 1],
      [1000, 1],
    ],
    showAtInitialZoom = false,
  }) {
    /*
    showAtInitialZoom -> determines if layer should be shown initially regardless of the specified keyframes
    */

    this.keyFramesOpacity = keyFramesOpacity;
    this._showAtInitialZoom = showAtInitialZoom;
    this.opacityScaling = d3
      .scaleLinear()
      .domain(keyFramesOpacity.map(keyFrame => keyFrame[0]))
      .range(keyFramesOpacity.map(keyFrame => keyFrame[1]));

    this.name = name;
    this.elements = elements;
    this.indices = {};
  }

  showAtInitialZoom(): boolean {
    return this._showAtInitialZoom;
  }

  getName(): string {
    return this.name;
  }

  drawElementsInViewPort(
    viewPort,
    ctx,
    transform,
    scaling,

    renderingState: RenderingState,
  ) {
    let viewPortElements = [];

    viewPortElements = this.getElementsInViewPort(viewPort);

    for (const element of viewPortElements) {
      element.draw(ctx, transform, scaling);
      ctx.beginPath();
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
  }

  isVisible(zoomLevel) {
    return (
      zoomLevel >= this.keyFramesOpacity[0][0] &&
      zoomLevel <= _.last(this.keyFramesOpacity)[0]
    );
  }

  getOpacity(zoomLevel) {
    return this.opacityScaling(zoomLevel);
  }

  registerElement(element) {
    this.elements.push(element);
    for (const index of _.values(this.indices)) {
      (index as any).addElement(element);
    }
  }

  addIndex(name, index_func) {
    this.indices[name] = new VirtualDomIndex(name, index_func);
  }

  getElementsForIndexValue(name, value) {
    if (name in this.indices) {
      return this.indices[name].getElementsForIndexValue(value);
    } else {
      return [];
    }
  }

  updateDimensionsForUrl(image) {
    for (const element of this.getElements()) {
      if (element.url === image.src) {
        element.setAttribute('width', image.naturalWidth);
        element.setAttribute('height', image.naturalHeight);
      }
    }
  }

  getElementsInViewPort(viewport) {
    return this.elements.filter(element => {
      const [x, y] = [element.getAttribute('x'), element.getAttribute('y')];

      if (
        x + element.getAttribute('width') < viewport.xmin ||
        x - element.getAttribute('width') > viewport.xmax ||
        y + element.getAttribute('height') < viewport.ymin ||
        y - element.getAttribute('height') > viewport.ymax
      ) {
        return false;
      } else {
        return true;
      }
    });
  }

  getStaticElementsInViewPort(viewport, transform) {
    /*
    returns all Elements in viewPort, assuming that all elements have static size (e.g. don't scale when zooming in)
    */
    return this.elements.filter(element => {
      const [x, y] = [element.getAttribute('x'), element.getAttribute('y')];

      const [width, height] = [
        element.get('width') / transform.k,
        element.get('height') / transform.k,
      ];
      if (
        x + width < viewport.xmin ||
        x - width > viewport.xmax ||
        y + height < viewport.ymin ||
        y - height > viewport.ymax
      ) {
        return false;
      } else {
        return true;
      }
    });
  }

  getElements() {
    return this.elements;
  }
}
